import { ConditionalLink } from "components/conditionalLink";
import { DashboardLayout } from "components/DashboardLayout";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { Search } from "components/DesignSystem/Search/Search";
import Tab from "components/DesignSystem/Tab/Tab";
import { ProductCard } from "components/ProductCard/ProductCard";
import { ALL, COMPLETED, ONGOING } from "constants/task";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { ServicesTable } from "pages/Books/Services/ServicesTable";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useGetBaseTaskTemplatesQuery } from "store/apis/getTemplates";
import { useGetTaskListForUserQuery } from "store/apis/taskList";
import { TemplateType } from "store/slices/IntragroupService";
import {
  openSalesTaxTask,
  setCallbackUrlOnModalClose,
  setSalesTaxTaskCount,
} from "store/slices/SalesTaxService";
import { RootState } from "store/store";

const SalesTaxServicesCards = () => {
  const { data, isLoading, isSuccess } = useGetBaseTaskTemplatesQuery({
    task_category_type: "SALES_TAX",
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const taskTemplates = data?.["Sales Tax"];
  const isEmpty = taskTemplates?.length === 0;

  const openSalesTaxTaskLaunch = (taskTemplate: TemplateType) => {
    history.push("/sales-tax/services/addtask");
    dispatch(setCallbackUrlOnModalClose("/sales-tax/services"));
    dispatch(openSalesTaxTask(taskTemplate));
  };

  return (
    <Async.Root isEmpty={isEmpty} isLoading={isLoading} isSuccess={isSuccess}>
      <Async.Success>
        <div className="t-grid lg:t-grid-cols-2 t-gap-5">
          {taskTemplates?.map((taskTemplate) => {
            const { title, description, approx_price, uuid } = taskTemplate;
            return (
              <ProductCard
                key={uuid}
                action={
                  <Button
                    size="small"
                    onClick={() =>
                      openSalesTaxTaskLaunch(
                        taskTemplate as unknown as TemplateType
                      )
                    }
                  >
                    Start now
                  </Button>
                }
                price={<AmountSuperScript amount={Number(approx_price)} />}
                {...{ title, description }}
              />
            );
          })}
        </div>
      </Async.Success>
      <Async.Empty>
        <></>
      </Async.Empty>
    </Async.Root>
  );
};

export const SalesTaxServices = () => {
  usePageTitle("Sales tax");
  const { update } = useUpdateQuery();
  const query = useQuery();
  const serviceType = query.get("salestax");
  const search = query.get("search");
  const rRef = useRef(null);
  const { ongoing_count, count, completed_count } = useSelector(
    (state: RootState) => state.salexTaxList.salesTaxTaskCounts
  );
  const dispatch = useDispatch();

  const { data, isLoading, isSuccess } = useGetTaskListForUserQuery({
    taskCategoryType: "SALES_TAX",
  });

  useEffect(() => {
    dispatch(
      setSalesTaxTaskCount({
        ongoing_count: data?.ongoing_count || 0,
        count: data?.count || 0,
        completed_count: data?.completed_count || 0,
      })
    );
  }, [data?.count]);

  const handleTabChange = (value: string) => {
    update({ query: "salestax", value: value || null });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search", value: value || null });
  };

  const tabs = [
    { name: "All", value: "ALL", taskCount: count },
    { name: "Ongoing", value: "ONGOING", taskCount: ongoing_count },
    {
      name: "Completed",
      value: "COMPLETED",
      taskCount: completed_count,
    },
  ];

  const isEmpty = data?.count === 0;

  return (
    <Async.Root isEmpty={isEmpty} isLoading={isLoading} isSuccess={isSuccess}>
      <Async.Success>
        <Tab.Root
          asChild
          defaultValue="ALL"
          onValueChange={handleTabChange}
          value={serviceType || "ALL"}
        >
          <DashboardLayout
            ref={rRef}
            header={
              <Header
                v2
                title="Sales Tax Services"
                right={
                  <ConditionalLink to={`/sales-tax/services/addtask`}>
                    <Button size="small" customType="primary">
                      Request a service
                    </Button>
                  </ConditionalLink>
                }
                bottom={
                  <Tab.List>
                    {tabs.map(({ name, value, taskCount }) => (
                      <Tab.Trigger
                        value={value}
                        key={value}
                        suffix={taskCount?.toString()}
                      >
                        {name}
                      </Tab.Trigger>
                    ))}
                  </Tab.List>
                }
              />
            }
          >
            <div className="t-w-1/2">
              <Search
                placeholder="Search..."
                onChange={handleChange}
                value={search || ""}
                block
              />
            </div>
            <div>
              <Tab.Content value={ALL} className="t-h-full">
                <ServicesTable
                  serviceType="salestax"
                  taskCategoryType="SALES_TAX"
                  parentRoute="/sales-tax/services"
                />
              </Tab.Content>
              <Tab.Content value={ONGOING} className="t-h-full">
                <ServicesTable
                  serviceType="salestax"
                  taskCategoryType="SALES_TAX"
                  parentRoute="/sales-tax/services"
                />
              </Tab.Content>
              <Tab.Content value={COMPLETED} className="t-h-full">
                <ServicesTable
                  serviceType="salestax"
                  taskCategoryType="SALES_TAX"
                  parentRoute="/sales-tax/services"
                />
              </Tab.Content>
            </div>
          </DashboardLayout>
        </Tab.Root>
      </Async.Success>
      <Async.Empty>
        <DashboardLayout ref={rRef} header={<Header v2 title="Services" />}>
          <SalesTaxServicesCards />
        </DashboardLayout>
      </Async.Empty>
    </Async.Root>
  );
};
